/**
 * FieldBuilder — Constructs a stadium-quality 3D football field on the XZ plane.
 *
 * Reads `background_layers` and `viewport` from the scene manifest and builds:
 *   - PBR grass with procedural color + normal map (mowed stripe pattern)
 *   - End zones with team color + label text on the field
 *   - Crisp yard lines and yard numbers as textured planes (not line geometry)
 *   - Hash marks
 *   - Goal posts at both ends
 *   - Sky gradient (hemisphere dome)
 *   - Low-poly stadium ring around the field for depth perception
 *
 * Coordinates: 1 unit = 1 yard. Field origin at (0, 0, 0), opposite corner at
 * (vp.width, 0, vp.height).
 */

import * as THREE from 'three';

function _hex(c, fallback = 0x2d7a2d) {
  if (!c) return fallback;
  return parseInt(String(c).replace('#', '').replace(/^0x/i, ''), 16) || fallback;
}

function _hexToRgb(hex) {
  return {
    r: (hex >> 16) & 0xff,
    g: (hex >> 8) & 0xff,
    b: hex & 0xff,
  };
}

function _makeGrassColorTexture(stripeColors, numStripes) {
  const canvas = document.createElement('canvas');
  canvas.width = 1024;
  canvas.height = 1024;
  const ctx = canvas.getContext('2d');
  const pxPerStripe = canvas.width / numStripes;

  for (let i = 0; i < numStripes; i++) {
    const base = stripeColors[i % stripeColors.length];
    const rgb = _hexToRgb(base);

    const grad = ctx.createLinearGradient(i * pxPerStripe, 0, (i + 1) * pxPerStripe, 0);
    grad.addColorStop(0, `rgb(${Math.max(0, rgb.r - 6)}, ${Math.max(0, rgb.g - 8)}, ${Math.max(0, rgb.b - 6)})`);
    grad.addColorStop(0.5, `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`);
    grad.addColorStop(1, `rgb(${Math.max(0, rgb.r - 6)}, ${Math.max(0, rgb.g - 8)}, ${Math.max(0, rgb.b - 6)})`);
    ctx.fillStyle = grad;
    ctx.fillRect(i * pxPerStripe, 0, pxPerStripe + 1, canvas.height);
  }

  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const data = imageData.data;
  for (let i = 0; i < data.length; i += 4) {
    const noise = (Math.random() - 0.5) * 18;
    data[i] = Math.max(0, Math.min(255, data[i] + noise));
    data[i + 1] = Math.max(0, Math.min(255, data[i + 1] + noise));
    data[i + 2] = Math.max(0, Math.min(255, data[i + 2] + noise));
  }
  ctx.putImageData(imageData, 0, 0);

  const tex = new THREE.CanvasTexture(canvas);
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.anisotropy = 8;
  return tex;
}

function _makeGrassNormalTexture() {
  const canvas = document.createElement('canvas');
  canvas.width = 256; canvas.height = 256;
  const ctx = canvas.getContext('2d');
  const img = ctx.createImageData(canvas.width, canvas.height);
  const data = img.data;
  for (let y = 0; y < canvas.height; y++) {
    for (let x = 0; x < canvas.width; x++) {
      const i = (y * canvas.width + x) * 4;
      const dx = (Math.random() - 0.5) * 0.6;
      const dy = (Math.random() - 0.5) * 0.6;
      const nz = Math.sqrt(Math.max(0.001, 1 - dx * dx - dy * dy));
      data[i] = Math.floor(((dx + 1) / 2) * 255);
      data[i + 1] = Math.floor(((dy + 1) / 2) * 255);
      data[i + 2] = Math.floor(((nz + 1) / 2) * 255);
      data[i + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);
  const tex = new THREE.CanvasTexture(canvas);
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  return tex;
}

function _makeNumberTexture(num) {
  const canvas = document.createElement('canvas');
  canvas.width = 256;
  canvas.height = 256;
  const ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  ctx.shadowColor = 'rgba(0,0,0,0.4)';
  ctx.shadowBlur = 8;
  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 200px "Helvetica Neue", Arial, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(String(num), canvas.width / 2, canvas.height / 2 + 6);

  const tex = new THREE.CanvasTexture(canvas);
  tex.minFilter = THREE.LinearFilter;
  tex.anisotropy = 8;
  return tex;
}

function _makeEndZoneSlabTexture(bgColor) {
  const canvas = document.createElement('canvas');
  canvas.width = 32; canvas.height = 32;
  const ctx = canvas.getContext('2d');
  const rgb = _hexToRgb(bgColor);
  ctx.fillStyle = `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  const tex = new THREE.CanvasTexture(canvas);
  tex.minFilter = THREE.LinearFilter;
  return tex;
}

function _makeEndZoneLabelTexture(text, textColor = '#ffffff') {
  // Transparent canvas with just the label text — applied to a small
  // sub-plane inside the end zone so the text keeps its natural
  // 4:1-ish aspect ratio. Previously the label was painted onto the
  // full end-zone material (10 yd × 53 yd plane), which stretched
  // the text horribly.
  const canvas = document.createElement('canvas');
  canvas.width = 1024;
  canvas.height = 256;
  const ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  ctx.fillStyle = textColor;
  ctx.font = 'bold 160px "Helvetica Neue", Arial, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.shadowColor = 'rgba(0,0,0,0.4)';
  ctx.shadowBlur = 8;
  ctx.fillText(text, canvas.width / 2, canvas.height / 2);

  const tex = new THREE.CanvasTexture(canvas);
  tex.minFilter = THREE.LinearFilter;
  tex.anisotropy = 8;
  return tex;
}

export default class FieldBuilder {
  constructor(scene, viewport) {
    this.scene = scene;
    this.vp = viewport || { width: 100, height: 53.3 };
    this.group = new THREE.Group();
    this.group.name = 'field';
    scene.add(this.group);
    this._sky = null;
    this._stadium = null;
  }

  build(layers) {
    this._sky = _buildSky(this.scene);
    this._stadium = _buildStadium(this.scene, this.vp);

    const cfgByType = {};
    (layers || []).forEach((layer) => {
      cfgByType[layer.layer_type] = layer.config || {};
    });

    this._grass(cfgByType.striped_grass);
    this._endZones(cfgByType.end_zones);
    this._yardLines(cfgByType.yard_lines);
    this._hashMarks(cfgByType.hash_marks);
    this._yardNumbers(cfgByType.yard_numbers);
    this._goalposts();
    this._sidelinePaint();
  }

  _grass(cfg) {
    const colors = (cfg?.colors || ['#2d7a2d', '#267026']).map((c) => _hex(c));
    const stripeW = (cfg?.stripe_width || 5);
    const numStripes = Math.ceil(this.vp.width / stripeW);

    const colorTex = _makeGrassColorTexture(colors, numStripes);
    const normalTex = _makeGrassNormalTexture();
    normalTex.repeat.set(this.vp.width / 2, this.vp.height / 2);

    const geo = new THREE.PlaneGeometry(this.vp.width, this.vp.height);
    const mat = new THREE.MeshStandardMaterial({
      map: colorTex,
      normalMap: normalTex,
      normalScale: new THREE.Vector2(0.6, 0.6),
      roughness: 0.85,
      metalness: 0.0,
      side: THREE.FrontSide,
    });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.rotation.x = -Math.PI / 2;
    mesh.position.set(this.vp.width / 2, 0, this.vp.height / 2);
    mesh.receiveShadow = true;
    this.group.add(mesh);
  }

  _endZones(cfg) {
    const homeColor = _hex(cfg?.home_color || '#1a3a6e');
    const awayColor = _hex(cfg?.away_color || '#8b1a1a');
    const ezW = 10;

    const makeZone = (color, x, label) => {
      // Solid-color end zone slab. Filling the whole plane with a
      // tiny solid texture (rather than a stretched label texture)
      // keeps the slab uniform.
      const slabTex = _makeEndZoneSlabTexture(color);
      const geo = new THREE.PlaneGeometry(ezW, this.vp.height);
      const mat = new THREE.MeshStandardMaterial({
        map: slabTex, side: THREE.FrontSide, roughness: 0.7, metalness: 0.0,
      });
      const mesh = new THREE.Mesh(geo, mat);
      mesh.rotation.x = -Math.PI / 2;
      mesh.position.set(x, 0.005, this.vp.height / 2);
      this.group.add(mesh);

      // Label sub-plane: ~7 yd wide × ~1.75 yd tall (4:1 native
      // aspect of the label canvas), centered in the slab. Rotated
      // so HOME reads from the home sideline (long axis runs along
      // the field length).
      if (label) {
        const labelTex = _makeEndZoneLabelTexture(label);
        const labelW = 7.0;
        const labelH = 1.75;
        const labelGeo = new THREE.PlaneGeometry(labelW, labelH);
        const labelMat = new THREE.MeshBasicMaterial({
          map: labelTex,
          transparent: true,
          depthWrite: false,
        });
        const labelMesh = new THREE.Mesh(labelGeo, labelMat);
        labelMesh.rotation.x = -Math.PI / 2;
        // Rotate label so it reads from the side opposite the
        // sideline the camera flies in from. Both end zones get
        // the same rotation so HOME on the left reads "→" and
        // AWAY on the right reads "←" when viewed top-down.
        labelMesh.rotation.z = (x < this.vp.width / 2) ? Math.PI / 2 : -Math.PI / 2;
        labelMesh.position.set(x, 0.012, this.vp.height / 2);
        this.group.add(labelMesh);
      }
    };

    makeZone(homeColor, ezW / 2, cfg?.home_label || 'HOME');
    makeZone(awayColor, this.vp.width - ezW / 2, cfg?.away_label || 'AWAY');
  }

  _yardLines(cfg) {
    const every = cfg?.every || 5;
    const lineWidth = 0.18;

    for (let yard = 10; yard <= this.vp.width - 10; yard += every) {
      const isMajor = yard % 10 === 0;
      const w = isMajor ? lineWidth * 1.4 : lineWidth;
      const geo = new THREE.PlaneGeometry(w, this.vp.height - 0.4);
      const mat = new THREE.MeshStandardMaterial({
        color: 0xfafafa,
        roughness: 0.55,
        metalness: 0.0,
      });
      const mesh = new THREE.Mesh(geo, mat);
      mesh.rotation.x = -Math.PI / 2;
      mesh.position.set(yard, 0.012, this.vp.height / 2);
      this.group.add(mesh);
    }

    const goalGeo = new THREE.PlaneGeometry(0.3, this.vp.height);
    const goalMat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.5 });
    [10, this.vp.width - 10].forEach((x) => {
      const m = new THREE.Mesh(goalGeo, goalMat);
      m.rotation.x = -Math.PI / 2;
      m.position.set(x, 0.015, this.vp.height / 2);
      this.group.add(m);
    });
  }

  _hashMarks(cfg) {
    const hashPositions = cfg?.positions || [0.354, 0.646];
    const dashLen = 0.6;
    const dashGeo = new THREE.PlaneGeometry(dashLen, 0.12);
    const dashMat = new THREE.MeshStandardMaterial({ color: 0xfafafa, roughness: 0.6 });

    for (let yard = 11; yard <= this.vp.width - 11; yard += 1) {
      hashPositions.forEach((pct) => {
        const z = pct * this.vp.height;
        const m = new THREE.Mesh(dashGeo, dashMat);
        m.rotation.x = -Math.PI / 2;
        m.position.set(yard, 0.012, z);
        this.group.add(m);
      });
    }
  }

  _yardNumbers(cfg) {
    const numbers = cfg?.numbers || [10, 20, 30, 40, 50, 40, 30, 20, 10];
    const yardPositions = cfg?.positions || [20, 30, 40, 50, 60, 70, 80, 90, 100].map((x) => x);

    const xs = (cfg?.positions || [10, 20, 30, 40, 50, 60, 70, 80, 90]).map((y) => y + 10);

    numbers.forEach((num, i) => {
      const x = xs[i] !== undefined ? xs[i] : yardPositions[i];
      const tex = _makeNumberTexture(num);
      const geo = new THREE.PlaneGeometry(4, 4);
      const mat = new THREE.MeshStandardMaterial({
        map: tex, transparent: true, side: THREE.FrontSide, roughness: 0.6, metalness: 0.0,
      });
      [{ z: this.vp.height * 0.13, flip: false }, { z: this.vp.height * 0.87, flip: true }].forEach(({ z, flip }) => {
        const m = new THREE.Mesh(geo, mat.clone());
        m.rotation.x = -Math.PI / 2;
        m.rotation.z = flip ? Math.PI : 0;
        m.position.set(x, 0.013, z);
        this.group.add(m);
      });
    });
  }

  _goalposts() {
    const yellow = 0xffd400;
    const postMat = new THREE.MeshStandardMaterial({
      color: yellow, roughness: 0.4, metalness: 0.5,
    });

    const make = (centerX, dirSign) => {
      const baseHeight = 3.0;
      const crossbarHeight = 3.05;
      const uprightHeight = 6.0;
      const uprightSpacing = 5.65;

      const baseGeo = new THREE.CylinderGeometry(0.08, 0.10, baseHeight, 12);
      const base = new THREE.Mesh(baseGeo, postMat);
      base.position.set(centerX + dirSign * 1.2, baseHeight / 2, this.vp.height / 2);
      this.group.add(base);

      const armGeo = new THREE.CylinderGeometry(0.07, 0.07, 1.5, 10);
      const arm = new THREE.Mesh(armGeo, postMat);
      arm.rotation.z = Math.PI / 2;
      arm.position.set(centerX + dirSign * 0.6, crossbarHeight, this.vp.height / 2);
      this.group.add(arm);

      const crossbarGeo = new THREE.CylinderGeometry(0.07, 0.07, uprightSpacing, 10);
      const crossbar = new THREE.Mesh(crossbarGeo, postMat);
      crossbar.rotation.x = Math.PI / 2;
      crossbar.position.set(centerX, crossbarHeight + 0.05, this.vp.height / 2);
      this.group.add(crossbar);

      const uprightGeo = new THREE.CylinderGeometry(0.07, 0.07, uprightHeight, 10);
      [-uprightSpacing / 2, uprightSpacing / 2].forEach((zOffset) => {
        const up = new THREE.Mesh(uprightGeo, postMat);
        up.position.set(centerX, crossbarHeight + uprightHeight / 2, this.vp.height / 2 + zOffset);
        this.group.add(up);
      });
    };

    make(2.0, -1);
    make(this.vp.width - 2.0, 1);
  }

  _sidelinePaint() {
    const w = this.vp.width;
    const h = this.vp.height;
    const stripeMat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.5 });

    const horizGeo = new THREE.PlaneGeometry(w, 0.3);
    const top = new THREE.Mesh(horizGeo, stripeMat);
    top.rotation.x = -Math.PI / 2;
    top.position.set(w / 2, 0.013, 0.15);
    this.group.add(top);
    const bot = top.clone();
    bot.position.set(w / 2, 0.013, h - 0.15);
    this.group.add(bot);
  }

  dispose() {
    this.group.traverse((obj) => {
      if (obj.geometry) obj.geometry.dispose();
      if (obj.material) {
        const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
        mats.forEach((m) => {
          if (m.map) m.map.dispose();
          if (m.normalMap) m.normalMap.dispose();
          m.dispose();
        });
      }
    });
    this.scene.remove(this.group);

    if (this._sky) {
      this._sky.parent && this._sky.parent.remove(this._sky);
      this._sky.geometry?.dispose();
      this._sky.material?.dispose();
    }
    if (this._stadium) {
      this._stadium.traverse((obj) => {
        if (obj.geometry) obj.geometry.dispose();
        if (obj.material) {
          const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
          mats.forEach((m) => m.dispose());
        }
      });
      this._stadium.parent && this._stadium.parent.remove(this._stadium);
    }
  }
}

function _buildSky(scene) {
  const skyGeo = new THREE.SphereGeometry(400, 32, 16);
  const uniforms = {
    topColor: { value: new THREE.Color(0x4d8fc8) },
    bottomColor: { value: new THREE.Color(0xeaf2fa) },
    offset: { value: 30 },
    exponent: { value: 0.55 },
  };
  const skyMat = new THREE.ShaderMaterial({
    uniforms,
    vertexShader: `
      varying vec3 vWorldPosition;
      void main() {
        vec4 worldPos = modelMatrix * vec4(position, 1.0);
        vWorldPosition = worldPos.xyz;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      uniform vec3 topColor;
      uniform vec3 bottomColor;
      uniform float offset;
      uniform float exponent;
      varying vec3 vWorldPosition;
      void main() {
        float h = normalize(vWorldPosition + vec3(0.0, offset, 0.0)).y;
        gl_FragColor = vec4(mix(bottomColor, topColor, max(pow(max(h, 0.0), exponent), 0.0)), 1.0);
      }
    `,
    side: THREE.BackSide,
    depthWrite: false,
  });
  const sky = new THREE.Mesh(skyGeo, skyMat);
  sky.name = 'sky';
  scene.add(sky);
  return sky;
}

function _buildStadium(scene, vp) {
  const group = new THREE.Group();
  group.name = 'stadium';

  const fieldCenter = new THREE.Vector3(vp.width / 2, 0, vp.height / 2);
  const innerOffsetX = vp.width / 2 + 12;
  const innerOffsetZ = vp.height / 2 + 8;

  const tierColors = [0x2a2a2a, 0x393939, 0x4a4a4a, 0x5a5a5a];
  const tierHeights = [3, 4, 5, 5.5];
  const tierThickness = 6;

  let cumulativeOffset = 0;
  tierColors.forEach((color, idx) => {
    const yBase = cumulativeOffset === 0 ? 0 : tierHeights.slice(0, idx).reduce((a, b) => a + b, 0);
    const h = tierHeights[idx];
    const xExt = innerOffsetX + cumulativeOffset;
    const zExt = innerOffsetZ + cumulativeOffset;
    const mat = new THREE.MeshStandardMaterial({ color, roughness: 0.85, metalness: 0.0 });

    const longGeo = new THREE.BoxGeometry((xExt + tierThickness) * 2, h, tierThickness);
    const top = new THREE.Mesh(longGeo, mat);
    top.position.set(fieldCenter.x, yBase + h / 2, fieldCenter.z + zExt + tierThickness / 2);
    group.add(top);
    const bot = new THREE.Mesh(longGeo, mat);
    bot.position.set(fieldCenter.x, yBase + h / 2, fieldCenter.z - zExt - tierThickness / 2);
    group.add(bot);

    const sideGeo = new THREE.BoxGeometry(tierThickness, h, zExt * 2);
    const left = new THREE.Mesh(sideGeo, mat);
    left.position.set(fieldCenter.x - xExt - tierThickness / 2, yBase + h / 2, fieldCenter.z);
    group.add(left);
    const right = new THREE.Mesh(sideGeo, mat);
    right.position.set(fieldCenter.x + xExt + tierThickness / 2, yBase + h / 2, fieldCenter.z);
    group.add(right);

    cumulativeOffset += tierThickness * 0.85;
  });

  const railMat = new THREE.MeshStandardMaterial({ color: 0xb0b0b0, roughness: 0.4, metalness: 0.4 });
  const railGeo = new THREE.BoxGeometry(1, 0.3, vp.height + innerOffsetZ * 0.4);
  const railL = new THREE.Mesh(railGeo, railMat);
  railL.position.set(fieldCenter.x - innerOffsetX, 0.5, fieldCenter.z);
  group.add(railL);
  const railR = railL.clone();
  railR.position.x = fieldCenter.x + innerOffsetX;
  group.add(railR);

  scene.add(group);
  return group;
}
